import { Dropdown, TabSelector } from 'holistic-resources';
import { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import OutstandingBalance from 'components/OutstandingBalance';
import SpeakToYourAgentCardShort from 'components/SpeakToYourAgentCardShort';
import SpeakToYourAgentCardWide from 'components/SpeakToYourAgentCardWide';
import Spinner from 'components/Spinner';
import DefaultHeader from 'components/pageLayout/DefaultHeader';
import Layout from 'components/pageLayout/Layout';
import PageContentContainer from 'components/pageLayout/PageContentContainer';
import useInitializeApp from 'hooks/useInitializeApp';
import useWhiteLabelSettings from 'hooks/useWhiteLabelSettings';
import { setSelectedBooking, setLatestBookings, setRefreshAfterPaymentFlag, toggleLoadingAppOverlay } from 'reducers/ui/uiSlice';
import { B2B_ENABLED } from 'shared/constants/appConfig';
import { getTabSelectorDropdownStyles } from 'shared/constants/dropdown';
import { fetchData } from 'shared/helpers/common';
import styles from '../../sass/style.module.scss';
import DocumentsTab from '../quoteBookingDetailsPageOverviewTab/DocumentsTab';
import OverviewTab from '../quoteBookingDetailsPageOverviewTab/OverviewTab';
import PassportsTab from './passportsTab/PassportsTab';
import { tabOptions as initialTabOptions, TAB_OVERVIEW, TAB_DOCUMENTS, TAB_PASSPORTS } from './constants';

export default function BookingsDetailsPage() {
  const { whiteLabelSettings = {} } = useWhiteLabelSettings();
  const { secondaryColour } = whiteLabelSettings;
  const [selectedTab, setSelectedTab] = useState(initialTabOptions[0]);
  const [localTabOptions, setLocalTabOptions] = useState(initialTabOptions);
  const [isLoading, setLoading] = useState(true);
  const selectedTabValue = selectedTab ? selectedTab.value : null;
  const [isLoyaltyBooking, setIsLoyaltyBooking] = useState(false);
  const { selectedBooking, loginCredentials, componentConfig, refreshAfterPaymentFlag, loggedOutFromTimeout, } = useSelector(
    (state) => state.ui,
  );
  const dispatch = useDispatch();

  const refreshBookingAfterPayment = useCallback(async () => {
    try {
      dispatch(setRefreshAfterPaymentFlag(false));
      const query = loginCredentials.reference ? `?reference=${loginCredentials.reference}` : '';
      const bookingData = await fetchData(
        `/api/self-service/composite/qb${query}`
      );

      const updatedBookingData = bookingData?.bookings?.results.find((booking) =>
        (selectedBooking.id && booking.id === selectedBooking.id) ||
        (selectedBooking.reference && booking.reference === selectedBooking.reference)
      );
  
      if (updatedBookingData) {
        dispatch(setSelectedBooking(updatedBookingData));
        dispatch(setLatestBookings(bookingData?.bookings?.results || []));
      } else {
        console.warn("No matching booking found to refresh");
      }
    } catch (error) {
      console.error(error);
    }
  }, [dispatch, loginCredentials?.reference, selectedBooking]);

  useInitializeApp(loginCredentials, dispatch);

  useEffect(() => {
    if (selectedBooking?.visitor) {
      setLoading(false);
    }
    
    let filteredTabs = initialTabOptions;
    if (!componentConfig.bookingDocumentsEnabled) {
      filteredTabs = filteredTabs.filter(item => item.value !== TAB_DOCUMENTS);
    }
    if (!componentConfig.bookingPassportsEnabled) {
      filteredTabs = filteredTabs.filter(item => item.value !== TAB_PASSPORTS);
    }
  
    setLocalTabOptions(prevTabs => 
      JSON.stringify(prevTabs) !== JSON.stringify(filteredTabs) ? filteredTabs : prevTabs
    );
  
    if (refreshAfterPaymentFlag) {
      refreshBookingAfterPayment();
    }
  }, [
    selectedBooking,
    componentConfig.bookingDocumentsEnabled,
    componentConfig.bookingPassportsEnabled,
    refreshAfterPaymentFlag,
    refreshBookingAfterPayment
  ]); 

  useEffect(() => {
    if (!loggedOutFromTimeout) {
      dispatch(toggleLoadingAppOverlay(false));
    }
  }, [loggedOutFromTimeout, dispatch]);

  useEffect(() => {
    if (selectedBooking?.tours?.length) {
      const foundLoyalty = selectedBooking.tours.some(tour => tour.loyalty === true);
      setIsLoyaltyBooking(foundLoyalty);
    } else {
      setIsLoyaltyBooking(false);
    }
  }, [selectedBooking]);

  return (
    <Layout>
      <DefaultHeader
        label={selectedBooking?.name || `Your Booking`}
        departureDate={isLoyaltyBooking ? null : selectedBooking?.outbound}
      />
      <PageContentContainer className="bg-white">
        <div className="lg:hidden">
          <Dropdown
            styles={getTabSelectorDropdownStyles(whiteLabelSettings)}
            options={localTabOptions.filter(option => option.value !== selectedTab?.value)} // Filter out selected tab
            value={selectedTab}
            onChange={(newTab) => {
              setSelectedTab(newTab);
            }}
          />
        </div>
        <div className="hidden lg:block">
          <TabSelector
            tabList={localTabOptions}
            selectedTabValue={selectedTabValue}
            onTabSelect={setSelectedTab}
            tabClassName="grow font-medium text-center pt-4 max-w-[20%]"
            containerClassName={`bg-white flex text-[18px] ${styles.bookingsTabSelector}`}
            selectedTabStyle={{
              color: secondaryColour,
            }}
            highlightBarStyle={{
              width: '100%',
              backgroundColor: secondaryColour,
              borderRadius: '99px',
              padding: '2px',
              marginTop: '15px',
            }}
          />
        </div>
      </PageContentContainer>
      {isLoading ? (
        <div className="text-center">
          <Spinner />
          Loading ...
        </div>
      ) : (
        <PageContentContainer className="bg-[#FAFAFA] pb-[40px] min-h-[calc(100vh-100px)]">
          <div className="mx-8 grid:mx-0 lg:flex md:items-start">
            <div className="grow lg:mr-[40px] lg:mb-14 w-full lg:w-[60%]">
              {selectedTab?.value === TAB_OVERVIEW && (
                <OverviewTab
                  data={selectedBooking}
                  refreshBookingAfterPayment={refreshBookingAfterPayment}
                  isBooking
                />
              )}
              {selectedTab?.value === TAB_DOCUMENTS && (
                <DocumentsTab data={selectedBooking} />
              )}
              {selectedTab?.value === TAB_PASSPORTS && (
                <PassportsTab booking={selectedBooking} />
              )}
              {!B2B_ENABLED && componentConfig.contactAgentEnabled &&
                <SpeakToYourAgentCardWide context="booking" />
              }
            </div>
            {!B2B_ENABLED &&
              <div className={`flex flex-col ${!componentConfig.contactAgentEnabled && 'mt-[35px]'}`}>
                {componentConfig.contactAgentEnabled &&
                  <SpeakToYourAgentCardShort context="booking" />
                }
                <OutstandingBalance data={selectedBooking} />
              </div>
            }
          </div>
        </PageContentContainer>
      )}
    </Layout>
  );
}